/**
 * Button  component props
 *
 * @package ext-components-base
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BP } from "@adapptio/lib-blueprint";

import {
	termsEditor as LibComponentsTerms,
	ButtonProps,
	BUTTON_ROLE,
	getValuesFromStringEnum,
	BUTTON_ROLE_default,
	LinkSchema,
	BackgroundColorSchema,
	ForegroundColorSchema,
	BorderColorSchema,
	propGroups,
	BorderRadiusSchema,
	SPACING,
	spacingPattern,
	SPACING_narrow,
	SPACING_default,
	ENUM_DEFAULT_VALUE
} from "@adapptio/lib-components";

export const ComponentButton_Props = {
	...ButtonProps,

	typeData: BP.Prop(
		BP.OneOf({
			...LibComponentsTerms.schemas.button.typeData,
			typeValueOpts: {
				...LibComponentsTerms.schemas.button.typeData
			},
			defaultType: "DEFAULT",
			types: {
				DEFAULT: {
					...LibComponentsTerms.schemas.button.typeDataValues.default,
					value: BP.Object({
						props: {
							role: BP.Prop(
								BP.Enum.String({
									...LibComponentsTerms.schemas.button.role,
									options: getValuesFromStringEnum(
										BUTTON_ROLE,
										LibComponentsTerms.schemas.button.roleValues
									),
									default: BUTTON_ROLE_default
								}),
								0
							)
						},
						editorOptions: {
							layoutType: "passthrough"
						}
					})
				},
				LINK: {
					...LibComponentsTerms.schemas.button.typeDataValues.link,
					value: LinkSchema({
						alias: "link_button",
						props: {
							enabled: BP.Prop(BP.Void({}))
						}
					})
				}
			},
			constraints: {
				required: true
			},
			editorOptions: {
				layoutType: "noHeader"
			}
		}),
		0,
		propGroups.type
	),

	styleData: BP.Prop(
		BP.OneOf({
			...LibComponentsTerms.schemas.button.styleData,
			typeValueOpts: {
				...LibComponentsTerms.schemas.button.styleData
			},
			defaultType: ENUM_DEFAULT_VALUE,
			types: {
				DEFAULT: {
					...LibComponentsTerms.schemas.button.styleDataValues.default,
					value: BP.Object({
						props: {
							backgroundColor: BP.Prop(
								BackgroundColorSchema({
									alias: "backgroundColor_button",
									default: "PRIMARY"
								}),
								10
							),

							foregroundColor: BP.Prop(
								ForegroundColorSchema({
									alias: "foregroundColor_button",
									default: "WHITE"
								}),
								20
							),

							hoverState: BP.Prop(
								BP.OptGroup({
									...LibComponentsTerms.schemas.button.styleDataHoverState,
									enabledOpts: {
										default: false,
										fallbackValue: false
									},
									value: BP.Object({
										props: {
											backgroundColor: BP.Prop(
												BackgroundColorSchema({
													alias: "backgroundColor_button",
													default: "PRIMARY"
												}),
												10
											),

											foregroundColor: BP.Prop(
												ForegroundColorSchema({
													alias: "foregroundColor_button",
													default: "WHITE"
												}),
												20
											)
										},
										editorOptions: {
											layoutType: "passthrough"
										}
									})
								}),
								30
							),

							activeState: BP.Prop(
								BP.OptGroup({
									...LibComponentsTerms.schemas.button.styleDataActiveState,
									enabledOpts: {
										default: false,
										fallbackValue: false
									},
									value: BP.Object({
										props: {
											backgroundColor: BP.Prop(
												BackgroundColorSchema({
													alias: "backgroundColor_button",
													default: "PRIMARY"
												}),
												10
											),

											foregroundColor: BP.Prop(
												ForegroundColorSchema({
													alias: "foregroundColor_button",
													default: "WHITE"
												}),
												20
											)
										},
										editorOptions: {
											layoutType: "passthrough"
										}
									})
								}),
								40
							)
						},
						editorOptions: {
							layoutType: "passthrough"
						}
					})
				},
				CLEAR: {
					...LibComponentsTerms.schemas.button.styleDataValues.clear,
					value: BP.Void({})
				},
				TAB: {
					...LibComponentsTerms.schemas.button.styleDataValues.tab,
					value: BP.Object({
						props: {
							backgroundColor: BP.Prop(
								BackgroundColorSchema({
									alias: "backgroundColor_buttonTabStyle",
									default: "TRANSPARENT"
								}),
								10
							),

							foregroundColor: BP.Prop(
								ForegroundColorSchema({
									alias: "foregroundColor_buttonTabStyle",
									default: "TEXT"
								}),
								20
							),

							borderColor: BP.Prop(
								BorderColorSchema({
									alias: "borderColor_buttonTabStyle",
									default: "TRANSPARENT"
								}),
								30
							),

							borderWidth: BP.Prop(
								BP.String({
									...LibComponentsTerms.schemas.common.borderWidth,
									default: "0px 0px 2px"
								}),
								40
							),

							borderRadius: BP.Prop(BorderRadiusSchema(), 50),

							padding: BP.Prop(
								BP.StringWithConst({
									...LibComponentsTerms.schemas.common.padding,
									constants: getValuesFromStringEnum(
										SPACING,
										LibComponentsTerms.schemas.common.spacingValues
									),
									default: `2px ${SPACING_narrow} 0px`,
									constraints: {
										...LibComponentsTerms.schemas.common.spacingConstraints,
										pattern: spacingPattern
									}
								}),
								60
							),

							hoverState: BP.Prop(
								BP.OptGroup({
									...LibComponentsTerms.schemas.button.styleDataHoverState,
									enabledOpts: {
										default: true,
										fallbackValue: true
									},
									value: BP.Object({
										props: {
											backgroundColor: BP.Prop(
												BackgroundColorSchema({
													alias: "backgroundColor_buttonTabStyleHoverState",
													default: "TRANSPARENT"
												}),
												10
											),

											foregroundColor: BP.Prop(
												ForegroundColorSchema({
													alias: "foregroundColor_buttonTabStyleHoverState",
													default: "PRIMARY"
												}),
												20
											),

											borderColor: BP.Prop(
												BorderColorSchema({
													alias: "borderColor_buttonTabStyleHoverState",
													default: "TRANSPARENT"
												}),
												30
											)
										},
										editorOptions: {
											layoutType: "passthrough"
										}
									})
								}),
								100
							),

							activeState: BP.Prop(
								BP.OptGroup({
									...LibComponentsTerms.schemas.button.styleDataActiveState,
									enabledOpts: {
										default: true,
										fallbackValue: true
									},
									value: BP.Object({
										props: {
											backgroundColor: BP.Prop(
												BackgroundColorSchema({
													alias: "backgroundColor_buttonTabStyleActiveState",
													default: "TRANSPARENT"
												}),
												10
											),

											foregroundColor: BP.Prop(
												ForegroundColorSchema({
													alias: "foregroundColor_buttonTabStyleActiveState",
													default: "PRIMARY"
												}),
												20
											),

											borderColor: BP.Prop(
												BorderColorSchema({
													alias: "borderColor_buttonTabStyleActiveState",
													default: "PRIMARY"
												}),
												30
											)
										},
										editorOptions: {
											layoutType: "passthrough"
										}
									})
								}),
								110
							)
						},
						editorOptions: {
							layoutType: "passthrough"
						}
					})
				},
				CUSTOM: {
					...LibComponentsTerms.schemas.button.styleDataValues.custom,
					value: BP.Object({
						props: {
							backgroundColor: BP.Prop(
								BackgroundColorSchema({
									alias: "backgroundColor_button",
									default: "PRIMARY"
								}),
								10
							),

							foregroundColor: BP.Prop(
								ForegroundColorSchema({
									alias: "foregroundColor_button",
									default: "WHITE"
								}),
								20
							),

							borderColor: BP.Prop(BorderColorSchema(), 30),

							borderWidth: BP.Prop(
								BP.String({
									...LibComponentsTerms.schemas.common.borderWidth,
									default: ""
								}),
								40
							),

							borderRadius: BP.Prop(
								BorderRadiusSchema({
									default: "6px"
								}),
								50
							),

							padding: BP.Prop(
								BP.StringWithConst({
									...LibComponentsTerms.schemas.common.padding,
									constants: getValuesFromStringEnum(
										SPACING,
										LibComponentsTerms.schemas.common.spacingValues
									),
									default: `0px ${SPACING_default}`,
									constraints: {
										...LibComponentsTerms.schemas.common.spacingConstraints,
										pattern: spacingPattern
									}
								}),
								60
							),

							hoverState: BP.Prop(
								BP.OptGroup({
									...LibComponentsTerms.schemas.button.styleDataHoverState,
									enabledOpts: {
										default: false,
										fallbackValue: false
									},
									value: BP.Object({
										props: {
											backgroundColor: BP.Prop(
												BackgroundColorSchema({
													alias: "backgroundColor_button",
													default: "PRIMARY"
												}),
												10
											),

											foregroundColor: BP.Prop(
												ForegroundColorSchema({
													alias: "foregroundColor_button",
													default: "WHITE"
												}),
												20
											),

											borderColor: BP.Prop(BorderColorSchema(), 30)
										},
										editorOptions: {
											layoutType: "passthrough"
										}
									})
								}),
								100
							),

							activeState: BP.Prop(
								BP.OptGroup({
									...LibComponentsTerms.schemas.button.styleDataActiveState,
									enabledOpts: {
										default: false,
										fallbackValue: false
									},
									value: BP.Object({
										props: {
											backgroundColor: BP.Prop(
												BackgroundColorSchema({
													alias: "backgroundColor_button",
													default: "PRIMARY"
												}),
												10
											),

											foregroundColor: BP.Prop(
												ForegroundColorSchema({
													alias: "foregroundColor_button",
													default: "WHITE"
												}),
												20
											),

											borderColor: BP.Prop(BorderColorSchema(), 30)
										},
										editorOptions: {
											layoutType: "passthrough"
										}
									})
								}),
								110
							)
						},
						editorOptions: {
							layoutType: "passthrough"
						}
					})
				}
			},
			constraints: {
				required: true
			},
			editorOptions: {
				layoutType: "noHeader"
			}
		}),
		0,
		propGroups.style
	)
};

/**
 * Button type string
 */
//export type BUTTON_TYPE_string = keyof TGetBlueprintSchemaSpec<ISchemaConstObject<typeof ComponentButton_Props>>["typeData"]["value"];

/**
 * Button style string
 */
//export type BUTTON_STYLE_string = keyof TGetBlueprintSchemaSpec<ISchemaConstObject<typeof ComponentButton_Props>>["styleData"]["value"];
